/* ═══════════════════════════════════════════════════════════════
   CategoriesService — the module library's nested CATEGORY tree
   (migration 056, src/shared/module-categories.ts).

   Same cloud shape every entity in this app uses: cloudCache over the
   generic EC2 table API, so reads are cached and writes invalidate.
   Subscribe a component with `useTableSync(['module_categories'], load)`
   for OTHER clients' realtime changes; the method that performed a write
   is responsible for refetching its own view afterward (same pattern
   `moduleService.ts`'s rename/setArchived/updateDefinition already use —
   none of them emit a sync event either, they just invalidate and let the
   caller reload). No bespoke permission keys: every action rides
   TABLE_PERMS.module_categories → the SAME `modules` category as
   module_library (list/query = modules.view, create = modules.create,
   rename/move/reorder = modules.edit, delete = modules.delete).

   `list()` degrades gracefully against an older server that hasn't taken
   migration 056 yet (desktop clients and the EC2 box don't always update in
   lockstep): a failed fetch resolves to an EMPTY category list plus a
   logged error, never a thrown error that would also break the module list
   sitting next to it on the same page.
   ═══════════════════════════════════════════════════════════════ */

import { resolveOrPlanCategoryPath, type ModuleCategory } from '../../shared/module-categories';
import * as cache from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const TABLE = 'module_categories';

const byName = (a: ModuleCategory, b: ModuleCategory) =>
  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

export const CategoriesService = {
  /** Every category the firm has. Degrades to `[]` (logged, not thrown) if
   *  the server hasn't got migration 056 yet — see the file header. */
  listAll: async (): Promise<ModuleCategory[]> => {
    try {
      const rows = await cache.list<ModuleCategory>(TABLE);
      return [...rows].sort(byName);
    } catch (err) {
      console.error(
        '[CategoriesService] module_categories failed to load (older server, or a transient ' +
          'error) — treating as an empty category list so the module library itself still loads',
        err
      );
      return [];
    }
  },

  get: async (id: string): Promise<ModuleCategory | null> =>
    (await CategoriesService.listAll()).find((c) => c.id === id) || null,

  /** CREATE a category (root, or under `parentId`). Server-validated: name
   *  1–80 trimmed chars, parent in the same firm, no cycle (impossible for
   *  a brand-new row, checked anyway), depth <= 8, sibling name conflict
   *  (409, same name/parent as an existing category — case-insensitive). */
  create: async (
    input: { name: string; parentId?: string | null; sortOrder?: number },
    userName?: string
  ): Promise<ModuleCategory> => {
    const now = new Date().toISOString();
    const saved = await cache.save<ModuleCategory & { id: string }>(TABLE, {
      id: crypto.randomUUID(),
      name: input.name,
      parentId: input.parentId ?? null,
      sortOrder: input.sortOrder ?? 0,
      createdBy: userName,
      modifiedBy: userName,
      lastModified: now,
    } as ModuleCategory & { id: string });
    logActivity({
      action: 'module_category.create',
      entityType: 'module_category',
      entityId: saved.id,
      metadata: { name: saved.name, parentId: saved.parentId ?? null },
    });
    return saved;
  },

  /** Rename only — parent/sort order untouched. Same 400/409 validation as
   *  `create` runs server-side against the new name at the category's
   *  CURRENT parent. */
  rename: async (id: string, name: string, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      TABLE,
      { name: name.trim(), modified_by: userName || null, last_modified: new Date().toISOString() },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidateAndNotify(TABLE);
    logActivity({
      action: 'module_category.rename',
      entityType: 'module_category',
      entityId: id,
      metadata: { name: name.trim() },
    });
  },

  /** Move (reparent) only — name/sort order untouched. `parentId: null`
   *  moves it to the root. Server refuses a cycle, a depth > 8 result, or
   *  a name conflict at the destination (409 — rename first, or the UI can
   *  offer the auto-suffix the delete path uses). */
  move: async (id: string, parentId: string | null, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      TABLE,
      {
        parent_id: parentId,
        modified_by: userName || null,
        last_modified: new Date().toISOString(),
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidateAndNotify(TABLE);
    logActivity({
      action: 'module_category.move',
      entityType: 'module_category',
      entityId: id,
      metadata: { parentId },
    });
  },

  /**
   * Reorder a set of SIBLINGS (same parent) to match `orderedIds`.
   * Sequential, AWAITED writes — one `sort_order` patch per row, never two
   * unawaited saves in flight — so a failure midway leaves a well-defined
   * prefix applied rather than a race between rows.
   */
  reorder: async (orderedIds: string[], userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    try {
      for (let i = 0; i < orderedIds.length; i++) {
        await cloudApi.dbUpdate(
          TABLE,
          {
            sort_order: i,
            modified_by: userName || null,
            last_modified: new Date().toISOString(),
          },
          [
            { col: 'firm_id', op: '=', val: firmId },
            { col: 'id', op: '=', val: orderedIds[i] },
          ]
        );
      }
    } finally {
      // Even a failure partway leaves a well-defined PREFIX applied server-
      // side (see the method doc above) — the cache must never keep serving
      // the pre-reorder order after that, so this runs whether the loop
      // completed or threw.
      cache.invalidateAndNotify(TABLE);
    }
    logActivity({
      action: 'module_category.reorder',
      entityType: 'module_category',
      metadata: { count: orderedIds.length },
    });
  },

  /**
   * Type-to-create support (pickers): given a human-typed path's segments
   * and the CURRENT category list, creates every level `resolveOrPlanCategoryPath`
   * (shared/module-categories.ts — the one place that plans existing-vs-missing)
   * says is missing, root to leaf, reusing whatever already exists. Sequential,
   * AWAITED creates — same reasoning as `reorder`: a failure partway leaves a
   * well-defined prefix created rather than a race. Returns the leaf id, ready
   * to assign to a module.
   */
  resolveOrCreatePath: async (
    names: string[],
    categories: ModuleCategory[],
    userName?: string
  ): Promise<string> => {
    const plan = resolveOrPlanCategoryPath(names, categories);
    let parentId = plan.existingId;
    for (const name of plan.toCreate) {
      const created = await CategoriesService.create({ name, parentId }, userName);
      parentId = created.id;
    }
    if (!parentId) throw new Error('That category path could not be created');
    return parentId;
  },

  /**
   * DELETE. Never deletes a module (law) — the server reparents the
   * category's own modules and direct child categories up to ITS parent
   * (NULL at the root) in one transaction before removing the row; a
   * promoted child whose name collides at the new level is auto-suffixed
   * server-side. Both affected caches are dropped wholesale (reparented
   * rows can't be patched individually from the response alone) — the
   * caller reloads whatever it's showing, same as every other write here.
   */
  remove: async (id: string): Promise<{ movedModules: number; reparentedCategories: number }> => {
    const res = await cache.remove(TABLE, id);
    // `cache.remove` above already fires the sync event for TABLE itself;
    // module_library also changed (the server reparented every module that
    // was filed under the deleted category) and needs its own notice.
    cache.invalidate(TABLE);
    cache.invalidateAndNotify('module_library');
    logActivity({ action: 'module_category.delete', entityType: 'module_category', entityId: id });
    return {
      movedModules: res?.movedModules || 0,
      reparentedCategories: res?.reparentedCategories || 0,
    };
  },
};
